import { useState } from 'react';
import { Images, Plus, Shuffle, Sparkles, Trash2, X } from 'lucide-react';
import { useStore, DOC_KEY } from '@/state/store';
import { TOOLS } from '@/tools/registry';
import { assetStore } from '@/media/assetStore';
import { useAssetVersion } from '@/hooks/useAssets';
import { useAddPhotos } from '@/hooks/useAddPhotos';
import { filesFromDataTransfer } from '@/media/pick';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Controls';
import { beginAssetDrag, endDrag } from '@/lib/dragPayload';
import { cn, formatBytes } from '@/lib/utils';

/** `onPicked` lets the mobile shell dismiss the drawer once a photo lands. */
export function PhotoLibrary({ onPicked }: { onPicked?: () => void } = {}) {
  useAssetVersion();
  const tool = useStore((s) => s.tool);
  // The photo library always shows the *active* tool's assets — each document
  // owns its own, so switching tools never shows another tool's photos.
  const assetOrder = useStore((s) => s[DOC_KEY[s.tool]].assetOrder);
  const assets = useStore((s) => s[DOC_KEY[s.tool]].assets);
  const slots = useStore((s) => s.project.slots);
  const source = useStore((s) => s.splitter.source.assetId);
  // Select the array itself, never a mapped copy: a selector that builds a new
  // array on every call never compares equal and re-renders forever.
  const layers = useStore((s) => s.carousel.layers);
  const deleteAsset = useStore((s) => s.deleteAsset);
  const clearLibrary = useStore((s) => s.clearLibrary);
  const autoFill = useStore((s) => s.autoFill);
  const addPhotos = useAddPhotos();
  const [over, setOver] = useState(false);

  const totalBytes = assetOrder.reduce((sum, id) => sum + (assets[id]?.size ?? 0), 0);
  // Which photos are already in use — a dot on the thumbnail. What "in use"
  // means differs per tool, so each tool answers for itself.
  const used = new Set(
    tool === 'collage'
      ? Object.values(slots).map((s) => s.assetId).filter(Boolean)
      : tool === 'splitter'
        ? [source]
        : layers.map((l) => (l.type === 'image' ? l.assetId : null)),
  );

  // Touch has no drag-and-drop, so a tap places the photo — what that means is
  // the tool's business.
  const place = (assetId: string) => {
    TOOLS[tool].placeAsset(assetId);
    onPicked?.();
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between px-3.5 py-3 hairline-b">
        <div className="flex items-baseline gap-2">
          <h2 className="text-[13px] font-semibold tracking-[-0.01em]">{TOOLS[tool].libraryLabel}</h2>
          {assetOrder.length > 0 && (
            <span className="font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">
              {assetOrder.length}
            </span>
          )}
        </div>
        <Button size="sm" variant="primary" icon={<Plus size={13} />} onClick={() => addPhotos()}>
          Add
        </Button>
      </header>

      <div
        className={cn(
          'scroll-thin flex-1 overflow-y-auto p-2.5 transition-colors duration-150',
          over && 'bg-[var(--accent-soft)]',
        )}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const files = filesFromDataTransfer(e.dataTransfer);
          if (files.length) void addPhotos(files);
        }}
      >
        {assetOrder.length === 0 ? (
          <button
            onClick={() => addPhotos()}
            className="mt-2 flex w-full flex-col items-center rounded-[14px] border border-dashed border-[var(--hairline-strong)] py-2 transition hover:border-[var(--accent)] hover:bg-[var(--accent-soft)]"
          >
            <EmptyState
              icon={<Images size={26} strokeWidth={1.4} />}
              title="Drag photos here"
              hint="or click to browse. JPEG, PNG, WebP, HEIC and GIF are supported."
            />
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-2 min-[420px]:grid-cols-4 lg:grid-cols-3">
            {assetOrder.map((id) => {
              const asset = assetStore.get(id);
              const meta = assets[id];
              if (!meta) return null;
              return (
                <div
                  key={id}
                  draggable
                  role="button"
                  tabIndex={0}
                  onDragStart={(e) => beginAssetDrag(e.dataTransfer, id)}
                  onDragEnd={endDrag}
                  onClick={() => place(id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      place(id);
                    }
                  }}
                  title={`${meta.name} · ${meta.width}×${meta.height} · ${formatBytes(meta.size)}`}
                  className={cn(
                    'group relative aspect-square cursor-grab overflow-hidden rounded-[10px]',
                    'bg-[var(--bg-sunken)] ring-1 ring-[var(--hairline)] transition-all duration-150',
                    'hover:ring-[var(--accent)] active:scale-[0.97]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
                  )}
                >
                  {asset && (
                    <img
                      src={asset.url}
                      alt={meta.name}
                      draggable={false}
                      className="h-full w-full object-cover"
                    />
                  )}
                  {used.has(id) && (
                    <span className="absolute bottom-1 left-1 h-1.5 w-1.5 rounded-full bg-[var(--accent)] shadow-[0_0_0_2px_rgba(255,255,255,0.85)]" />
                  )}
                  <button
                    aria-label={`Remove ${meta.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAsset(id);
                    }}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 focus-visible:opacity-100 hover:bg-black/75 [@media(hover:none)]:opacity-100"
                  >
                    <X size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {assetOrder.length > 0 && (
        <footer className="flex flex-col gap-2 px-2.5 py-2.5 hairline-t">
          <div className="flex gap-1.5">
            {tool === 'collage' ? (
              <>
                <Button
                  size="sm"
                  className="flex-1"
                  icon={<Sparkles size={13} />}
                  onClick={() => autoFill()}
                >
                  Auto-fill
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  icon={<Shuffle size={13} />}
                  onClick={() => autoFill({ shuffle: true, overwrite: true })}
                >
                  Shuffle
                </Button>
              </>
            ) : (
              <p className="flex-1 self-center text-[12px] leading-snug text-[var(--text-tertiary)]">
                {TOOLS[tool].libraryHint}
              </p>
            )}
            <IconButton label="Remove all photos" tone="danger" onClick={clearLibrary}>
              <Trash2 size={14} />
            </IconButton>
          </div>
          <p className="text-center font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">
            {formatBytes(totalBytes)} in library
          </p>
        </footer>
      )}
    </div>
  );
}
